// Arena colour shades (territory-visuals.md §0): every shade of a player colour comes from its
// HSV value V (0–100) with hue and saturation unchanged —
//   side = V × 0.75 (land and head rims, the raised edge), base = V × 0.5 (head under-block,
//   capture label), plate = base if V > 50 else max(2V, V + (100 − V)/2) (text on own-coloured
//   pills), particle = V set to 100, 90 … 20 (9 shades: break-up and chips).
// Scaling V with H and S fixed is the same as multiplying R, G and B by the factor, so side and
// base are plain RGB multiplies (scaleRgb). Pure; cached per colour.

import { ARENA_CLIENT, type Rgb, rgbChannels, rgbFromChannels, scaleRgb } from '@ballio/shared';

export interface ArenaShades {
  readonly main: Rgb;
  readonly side: Rgb;
  readonly base: Rgb;
  readonly plate: Rgb;
  /** V = 100, 90, …, 20. */
  readonly particles: readonly Rgb[];
}

/** HSV value 0–100 of a colour. */
export function valueOf(c: Rgb): number {
  const [r, g, b] = rgbChannels(c);
  return (Math.max(r, g, b) / 255) * 100;
}

/** The colour with its HSV value set to `v` (0–100), hue and saturation kept. */
export function withValue(c: Rgb, v: number): Rgb {
  const [r, g, b] = rgbChannels(c);
  const max = Math.max(r, g, b);
  if (max === 0) {
    const g8 = (v / 100) * 255;
    return rgbFromChannels(g8, g8, g8);
  }
  const k = ((v / 100) * 255) / max;
  return rgbFromChannels(r * k, g * k, b * k);
}

/** The plate shade (text on own-coloured pills, the leaderboard rows). */
export function plateShade(c: Rgb): Rgb {
  const v = valueOf(c);
  if (v > 50) return scaleRgb(c, ARENA_CLIENT.baseShade);
  return withValue(c, Math.min(100, Math.max(2 * v, v + (100 - v) / 2)));
}

const cache = new Map<Rgb, ArenaShades>();

export function shadesOf(c: Rgb): ArenaShades {
  let s = cache.get(c);
  if (s) return s;
  const particles: Rgb[] = [];
  for (let v = 100; v >= 20; v -= 10) particles.push(withValue(c, v));
  s = Object.freeze({
    main: c,
    side: scaleRgb(c, ARENA_CLIENT.sideShade),
    base: scaleRgb(c, ARENA_CLIENT.baseShade),
    plate: plateShade(c),
    particles: Object.freeze(particles),
  });
  if (cache.size > 512) cache.clear();
  cache.set(c, s);
  return s;
}
